/**
 * Study Resource Staff Level Issue Service
 * Handles API calls for staff level issuing
 */

import Config from '../_config/config';

export interface StaffLevelIssue {
  Id: number;
  ResourceId: number;
  SchoolId?: number;
  RoleId?: number;
  UserId?: number;
  StartDate?: string;
  EndDate?: string;
  CreatedByUserId?: number;
  CreationDate?: string;
  schoolName?: string;
  roleName?: string;
  userName?: string;
}

export interface CreateStaffLevelIssueRequest {
  resourceId: number;
  schoolId?: number;
  roleId?: number;
  userIds: number[]; // Array of user IDs for multi-select
  startDate?: string;
  endDate?: string;
}

class StudyResourceStaffIssueService {
  /**
   * Get authorization headers with JWT token
   */
  private getAuthHeaders(): HeadersInit {
    let token: string | null = null;
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser && currentUser !== 'undefined') {
      try {
        token = JSON.parse(currentUser).token;
      } catch (e) {
        console.error('Failed to parse currentUser:', e);
      }
    }

    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  /**
   * Get all staff level issues for a resource
   */
  async getStaffLevelIssues(resourceId: number): Promise<StaffLevelIssue[]> {
    const response = await fetch(
      `${Config.nodeSiteUrl}v1/study-resource/staff-issue/${resourceId}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || `Failed to fetch staff level issues: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || [];
  }

  /**
   * Create staff level issue
   */
  async createStaffLevelIssue(
    issue: CreateStaffLevelIssueRequest
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(
      `${Config.nodeSiteUrl}v1/study-resource/staff-issue`,
      {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(issue),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Failed to create staff level issue: ${response.statusText}`);
    }

    return data;
  }

  /**
   * Update staff level issue
   */
  async updateStaffLevelIssue(
    id: number,
    issue: Partial<CreateStaffLevelIssueRequest>
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(
      `${Config.nodeSiteUrl}v1/study-resource/staff-issue/${id}`,
      {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(issue),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Failed to update staff level issue: ${response.statusText}`);
    }

    return data;
  }

  /**
   * Delete staff level issue
   */
  async deleteStaffLevelIssue(id: number): Promise<{ success: boolean; message: string }> {
    const response = await fetch(
      `${Config.nodeSiteUrl}v1/study-resource/staff-issue/${id}`,
      {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Failed to delete staff level issue: ${response.statusText}`);
    }

    return data;
  }

  /**
   * Get all roles
   */
  async getRoles(): Promise<{ roleId: number; roleName: string }[]> {
    const response = await fetch(
      `${Config.nodeSiteUrl}v1/users/roles`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || `Failed to fetch roles: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || [];
  }

  /**
   * Get users by role, optionally scoped to a school
   */
  async getUsersByRole(
    roleId: number,
    schoolId?: number
  ): Promise<{ userId: number; userName: string }[]> {
    const qs = new URLSearchParams({ roleId: String(roleId) });
    if (schoolId !== undefined && schoolId !== null) {
      qs.append('schoolId', String(schoolId));
    }
    const response = await fetch(
      `${Config.nodeSiteUrl}v1/users/by-role?${qs.toString()}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || `Failed to fetch users: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || [];
  }
}

// Export singleton instance
export const studyResourceStaffIssueService = new StudyResourceStaffIssueService();
export default studyResourceStaffIssueService;
